import {
  CIMSModuleItem,
  CIMSPageItem,
  resolveValueOrArray,
} from '@cims-online/shared'
import { SimplePermissions } from 'hooks/useCheckPermission'
import { ItemType } from 'antd/lib/menu/hooks/useItems'
import ProjectLink from 'components/ProjectLink'
import { useAuthContext } from 'context/AuthContext'
import { useCallback } from 'react'
import { useProjectContext } from 'context/ProjectContext'

/**
 * Creates a project navigation module containing only pages the current user
 * may access. Returns `null` when project context is unavailable or the user
 * cannot access any page in the module.
 *
 * @see {@link ProjectSideNav}
 */
export function useProtectedMenuItem({
  icon,
  pages,
  ...rest
}: Module): ItemType {
  const { projectId } = useProjectContext()
  const { user } = useAuthContext()

  const authorized = useCallback(
    (permission: Page['permission'] = false) => {
      if (!user) return false
      else if (typeof permission === 'boolean') return permission
      else if (permission === 'admin') return user.admin(projectId).authorized
      return user.someAuthorized(resolveValueOrArray(permission), projectId)
        .authorized
    },
    [projectId, user]
  )

  if (!user) return null

  /**
   * ProjectSideNav only serves project routes; developer navigation is composed
   * separately by DevSideNav and must not create project links without this ID.
   */
  if (!projectId) return null

  /**
   * Avoid empty module submenus, while still allowing users with partial access
   * to discover every page they are authorized to use.
   */
  if (pages.every(({ permission }) => !authorized(permission))) return null
  return {
    label: rest.module.name,
    key: rest.module.id,
    icon,
    children: pages.reduce((pages, { page, icon, permission }) => {
      if (!authorized(permission)) return pages

      const label = <ProjectLink item={page}>{page.name}</ProjectLink>
      pages.push({ key: page.id, label, icon })
      return pages
    }, Array<ItemType>()),
  }
}

type Module = {
  module: CIMSModuleItem
  pages: Page[]
  /**
   * @default <FolderOpenOutlined />
   */
  icon?: React.ReactNode
}

type Page = {
  permission: SimplePermissions
  page: CIMSPageItem
  /**
   * @default null
   */
  icon?: React.ReactNode
}
